import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { NotPermitted, PhotoInput } from '@/design';
import { ApiError } from '@/domains/api';
import { useCan } from '@/domains/auth';
import type { IAdminParticipantDetail } from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import { statusRefusal } from '../../refusal';
import {
  type ISaidNote,
  NOT_REREAD,
  NoteLine,
  OUT_OF_DATE,
  unanswered,
  unansweredNote
} from './said';

/**
 * The opened participant's photo, set, replaced and removed by staff from the hub's header (the
 * advisor record page's W10, ruling U11; the F8 plan's R1–R6), over survey-api's
 * `PUT` / `DELETE …/photo` (its SR-222 block 8). Every avatar on the page follows from the
 * re-read.
 *
 * **What is offered** (R2): "Upload a photo" while the participant has none, "Replace the photo"
 * and "Remove the photo" once it has — the one `PhotoInput`, renamed, so a reader is not handed a
 * different control after the first upload — for every kind of participant. **Remove is
 * confirmed** (R3): no route undoes it, and a migrated photo's original is held by nobody on
 * staff. Replace is not: choosing the file is the confirmation.
 *
 * **Nothing is judged here** (R4): no type or size check, since survey-api decides both by the
 * bytes; its refusals — 400, 413, 415, written for a reader — are said in its words beside the
 * upload button, by `PhotoInput`, and a Remove's beside the row.
 *
 * **What a write was told follows the hub's rules** (R5, `said.tsx`): a refusal is said and
 * re-reads nothing; a success re-reads the hub through the page's `reread`, which tells the
 * activity to read its new row too, and says so — adding `NOT_REREAD` where that read failed; a
 * 404 re-reads, since the participant is gone; an answer that never arrived re-reads and asks to
 * be checked, never as a refusal. **The card the write answers is not adopted** into the page,
 * though it is the participant as it reads after: the hub is one document read at one moment —
 * every member, the activity's cue — and only a re-read keeps it so. While either write is out,
 * both controls are held (R6).
 *
 * **The confirmation outlives its trigger.** A re-read can take the photo away under an open
 * dialog — Remove's own success, or another staffer's removal the poll brings — and the trigger
 * goes with it. So the dialog stays mounted whatever the photo, closes when the photo is gone,
 * and hands focus to the group's first control when its trigger is no longer there: unmounted
 * open, it dropped focus on the document's body and kept `confirming`, which opened it again,
 * unasked, the moment an upload brought the trigger back. A Remove also remounts `PhotoInput`,
 * whose refusal was about the file it refused, not about the page after a later write.
 *
 * **Both writes are `responses.annotate`** (SR-228): without it the controls are drawn off, under
 * one `NotPermitted` sentence in the row.
 */
export function PhotoControls({
  participant,
  reread,
  quiet = false
}: {
  participant: IAdminParticipantDetail;
  reread: () => Promise<boolean>;
  /** The record card's last line, link-weight (SR-407): the photo is set now and then, and two
   *  outlined buttons at the head of the card outweighed who the record is. */
  quiet?: boolean;
}) {
  const [note, setNote] = React.useState<Nullable<ISaidNote>>(null);
  const [uploading, setUploading] = React.useState(false);
  const [removing, setRemoving] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [removals, setRemovals] = React.useState(0);
  const { participantId, displayName: name, photoUri } = participant;
  const label = React.useId();
  const group = React.useRef<HTMLDivElement>(null);
  const trigger = React.useRef<HTMLButtonElement>(null);
  const permitted = useCan()('responses.annotate');
  const reasonId = React.useId();
  const notPermitted = permitted ? undefined : reasonId;

  React.useEffect(() => {
    if (!photoUri && !removing) setConfirming(false);
  }, [photoUri, removing]);

  // Resolves to the sentence `PhotoInput` says beside its button — a refusal — or to null,
  // every other outcome going to the row's note.
  const upload = async (file: File): Promise<Nullable<string>> => {
    setUploading(true);
    setNote(null);
    try {
      await participantsApi.setPhoto(participantId, file);
      setNote(done('Photo saved.', await reread()));
      return null;
    } catch (error) {
      const told = await failed(error, reread, 'Not saved');
      if (told) {
        setNote(told);
        return null;
      }
      return statusRefusal(error, { 403: FORBIDDEN }, 'Could not save the photo');
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    setNote(null);
    try {
      await participantsApi.clearPhoto(participantId);
      setNote(done('Photo removed.', await reread()));
    } catch (error) {
      setNote(
        (await failed(error, reread, 'Not removed')) ?? {
          tone: 'refused',
          title: 'Not removed',
          text: statusRefusal(error, { 403: FORBIDDEN }, 'Could not remove the photo')
        }
      );
    } finally {
      setRemoving(false);
      setConfirming(false);
      setRemovals((count) => count + 1);
    }
  };

  return (
    <div className="grid gap-2">
      <div
        ref={group}
        role="group"
        aria-labelledby={label}
        className={
          quiet
            ? 'flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs'
            : 'flex flex-wrap items-start gap-x-3 gap-y-2'
        }
      >
        <span
          id={label}
          className={quiet ? 'text-muted-foreground uppercase' : 'pt-1.5 font-medium'}
        >
          Photo
        </span>
        <PhotoInput
          key={removals}
          label={photoUri ? 'Replace the photo' : 'Upload a photo'}
          onChoose={upload}
          disabled={removing || !permitted}
          describedBy={notPermitted}
          quiet={quiet}
        />
        <AlertDialog open={confirming} onOpenChange={(open) => !removing && setConfirming(open)}>
          {photoUri && (
            <AlertDialogTrigger asChild>
              {/* `data-slot` said again: the trigger's own would replace the kit's, and with it
                  the zone's button weight (`global.css`) — beside "Replace the photo", which
                  keeps it. */}
              <Button
                ref={trigger}
                type="button"
                size="sm"
                variant={quiet ? 'link' : 'outline'}
                className={quiet ? 'h-auto p-0' : undefined}
                data-slot="button"
                disabled={uploading || removing || !permitted}
                aria-describedby={notPermitted}
              >
                Remove the photo
              </Button>
            </AlertDialogTrigger>
          )}
          <AlertDialogContent
            onCloseAutoFocus={(event) => {
              if (trigger.current?.isConnected) return;
              event.preventDefault();
              group.current?.querySelector('button')?.focus();
            }}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Remove the photo of {name}?</AlertDialogTitle>
              <AlertDialogDescription>
                The file stays in storage, but nothing on this page can bring it back — upload
                it again to restore it.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={removing}
                onClick={(event) => {
                  event.preventDefault();
                  void remove();
                }}
              >
                {removing ? 'Removing…' : 'Remove'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {!permitted && <NotPermitted id={reasonId} what="changing a participant’s photo" />}
      {note && <NoteLine note={note} />}
    </div>
  );
}

/** A 403 carries no body (AUTH-§11.2): a role that opened the photo was taken away since. */
const FORBIDDEN = 'Your roles may no longer change a participant’s photo — reload the page.';

function done(text: string, read: boolean): ISaidNote {
  return { tone: 'done', text: text + (read ? '' : NOT_REREAD) };
}

/**
 * The failures that re-read — an answer that never arrived, and a 404 — said as the row's note;
 * `null` for a refusal, which re-reads nothing and is the caller's to say.
 */
async function failed(
  error: unknown,
  reread: () => Promise<boolean>,
  title: string
): Promise<Nullable<ISaidNote>> {
  if (unanswered(error)) return unansweredNote(await reread(), 'photo');
  if (error instanceof ApiError && error.status === 404) {
    const read = await reread();
    return {
      tone: 'refused',
      title,
      text:
        'The system holds no such participant — the page was out of date. ' +
        (read ? 'It has been read again.' : OUT_OF_DATE)
    };
  }
  return null;
}
